/**
 * Dropdown component — Events and Methods
 *
 * Depends on: utility.js (Utility, BREAKPOINTS)
 * Must be loaded synchronously after utility.js and before global.js.
 *
 * HTML contract (do not change without updating JS):
 *   [is="drop-down"]          — root dropdown element
 *   .dropdown-toggle          — the trigger button inside the root
 *   .slideout-submenu         — optional back/close button inside the dropdown
 *   .dropdown.open            — open state class applied to the root
 *
 * @param {HTMLElement} element - Dropdown root element
 */
class Dropdown {
  static MQ_IPAD_PRO_LANDSCAPE = window.matchMedia(`(min-width: ${BREAKPOINTS.ipadProMin}px) and (max-width: ${BREAKPOINTS.ipadProMax}px)`);

  static collapse(dropdown) {
    if (!dropdown) return;

    Utility.CSS.remove(dropdown, 'open');

    const toggle = dropdown.querySelector('.dropdown-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');

    dropdown.querySelectorAll('.dropdown.open').forEach(child => {
      Utility.CSS.remove(child, 'open');
      const childToggle = child.querySelector('.dropdown-toggle');
      if (childToggle) childToggle.setAttribute('aria-expanded', 'false');
    });
  }

  constructor(element) {
    this.dropdown = element;
    this.toggleCTA = (Utility?.DomSelector?.$('.dropdown-toggle', element)) || element.querySelector('.dropdown-toggle');
    /** In the mobile nav drawer, only tap/click should open (no mouse hover or focus-based open/close). */
    this.isMobileNavDrawer = !!(element && element.closest && element.closest('.mobile-nav-drawer'));
    this.supportedEvents = ["click", "touchstart", "touchmove", "touchend", "mouseenter", "mouseleave", "focusin", "focusout"];
    this.touchState = { startX: 0, startY: 0, moved: false };

    if (!this.toggleCTA) {
      console.warn('Dropdown toggle element not found:', element);
      return;
    }

    this.init();
  }

  init() {
    const handlers = {
      click: (evt) => {
        evt.preventDefault();
        this.toggleDropdown();
      },
      touchstart: (evt) => {
        const touch = evt.touches?.[0];
        if (!touch) return;
        this.touchState.startX = touch.clientX;
        this.touchState.startY = touch.clientY;
        this.touchState.moved = false;
      },
      touchmove: (evt) => {
        const touch = evt.touches?.[0];
        if (!touch || this.touchState.moved) return;

        const deltaX = Math.abs(touch.clientX - this.touchState.startX);
        const deltaY = Math.abs(touch.clientY - this.touchState.startY);
        if (deltaX > 10 || deltaY > 10) {
          this.touchState.moved = true;
        }
      },
      touchend: (evt) => {
        if (this.touchState.moved) {
          this.touchState.moved = false;
          return;
        }
        evt.preventDefault();
        this.toggleDropdown();
        this.touchState.moved = false;
      },
      mouseenter: () => {
        this.dropdownShow(this.dropdown);
        this.childOnFocusChange(this.dropdown);
      },
      mouseleave: () => {
        // Do not close on pointer leave if keyboard focus is still inside (e.g. wide megamenu panel).
        if (this.dropdown.contains(document.activeElement)) return;
        this.dropdownHide(this.dropdown);
      },
      focusin: (evt) => {
        const t = evt.target;
        if (!this.dropdown.contains(t)) return;

        const focusOnToggle =
          this.toggleCTA === t || (this.toggleCTA.contains && this.toggleCTA.contains(t));

        // Avoid mouse-click conflict: pointer focus on toggle fires focusin then click (toggle would close).
        // :focus-visible is false for pointer, true for Tab — so keyboard users still get an open submenu.
        if (focusOnToggle && !this.toggleCTA.matches(':focus-visible')) {
          return;
        }

        this.dropdownShow(this.dropdown);
        this.childOnFocusChange(this.dropdown);
      },
      focusout: () => {
        if (!this.dropdown.classList.contains('open')) return;
        // Defer so document.activeElement reflects the next focused node after Tab (relatedTarget is unreliable in some cases).
        requestAnimationFrame(() => {
          if (!this.dropdown.contains(document.activeElement)) {
            this.dropdownHide(this.dropdown);
          }
        });
      }
    };

    this.supportedEvents.forEach((type) => {
      if (this.isMobileNavDrawer) {
        if (type === 'mouseenter' || type === 'mouseleave' || type === 'focusin' || type === 'focusout') {
          return;
        }
      }
      const target =
        type === 'mouseleave' || type === 'focusin' || type === 'focusout' ? this.dropdown : this.toggleCTA;
      const handler = handlers[type];
      if (handler) target.addEventListener(type, handler, { passive: type === 'touchmove' });
    });

    this.dropdown.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape' || !this.dropdown.classList.contains('open')) return;
      if (!this.dropdown.contains(document.activeElement)) return;
      evt.preventDefault();
      evt.stopPropagation();
      this.dropdownHide(this.dropdown);
      if (this.toggleCTA && typeof this.toggleCTA.focus === 'function') {
        this.toggleCTA.focus();
      }
    });

    this.bindEvents();
  }

  bindEvents() {
    // Mobile `.slideout-submenu` handlers: see `setupMobileNavSlideoutCapture` (document capture).
  }

  /**
   * Toggle dropdown
   * @returns {Dropdown} Returns this for method chaining
   */
  toggleDropdown() {
    this.dropdownToggle(this.dropdown);
    return this;
  }

  /**
   * Toggle dropdown parent
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownToggle(dropdownParent) {
    setTimeout(() => {
      dropdownParent.classList.contains('open')
        ? this.dropdownHide(dropdownParent)
        : this.dropdownShow(dropdownParent);
    }, 50);
    return this;
  }

  /**
   * Show dropdown
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownShow(dropdownParent) {
    if (!dropdownParent) {
      return this;
    }

    if (Dropdown.MQ_IPAD_PRO_LANDSCAPE.matches && dropdownParent.parentElement) {
      dropdownParent.parentElement
        .querySelectorAll('[is="drop-down"].open')
        .forEach((openDropdown) => {
          if (openDropdown !== dropdownParent) Dropdown.collapse(openDropdown);
        });
    }

    this.toggleCTA.setAttribute('aria-expanded', true);
    Utility.CSS.add(dropdownParent, 'open');
    return this;
  }

  /**
   * Hide dropdown
   * @param {HTMLElement} dropdownParent
   * @returns {Dropdown} Returns this for method chaining
   */
  dropdownHide(dropdownParent) {
    if (!dropdownParent) return this;

    Dropdown.collapse(dropdownParent);

    return this;
  }

  childOnFocusChange(dropdownParent) {
    const openDropdowns = Array.from(dropdownParent.parentElement.querySelectorAll('.dropdown.open'));
    const currentIndex = openDropdowns.indexOf(dropdownParent);
    openDropdowns.forEach((ele) => {
      const eleIndex = openDropdowns.indexOf(ele);
      if (eleIndex !== currentIndex) {
        Dropdown.collapse(ele);
      }
    });
    return this;
  }
}

/**
 * Mobile drawer megamenus nest `[is="drop-down"]` panels. Each `.slideout-submenu` lives inside
 * `.submenu`, whose parent is always that panel’s `[is="drop-down"]` (navigation-mobile-item.liquid).
 * Document capture + `Dropdown.collapse(owner)` avoids the wrong ancestor closing (jump to first level).
 *
 * Mobile: back controls are `<a>` without `href`; iOS/WebKit often omit or delay `click`. We handle
 * `touchend` (capture, passive: false) and dedupe the synthetic `click` that follows.
 */
(function setupMobileNavSlideoutCapture() {
  const TAP_SLOP_PX = 18;
  let suppressClickUntil = 0;
  /** @type {{ active: boolean, x: number, y: number, owner: Element | null }} */
  let touchTrack = { active: false, x: 0, y: 0, owner: null };

  const resetTouchTrack = () => {
    touchTrack = { active: false, x: 0, y: 0, owner: null };
  };

  function ownerFromSlideout(back) {
    const submenu = back.closest('.submenu');
    if (submenu && submenu.parentElement && submenu.parentElement.matches('[is="drop-down"]')) {
      return submenu.parentElement;
    }
    return back.closest('[is="drop-down"]');
  }

  function resolveBackTarget(t) {
    if (!t || !t.closest) return null;
    const drawer = t.closest('.mobile-nav-drawer');
    if (!drawer) return null;
    const back = t.closest('.slideout-submenu');
    if (!back || !drawer.contains(back)) return null;
    const owner = ownerFromSlideout(back);
    if (!owner || !owner.classList.contains('open')) return null;
    return { back, owner };
  }

  function collapseSlideout(evt, owner) {
    evt.preventDefault();
    evt.stopImmediatePropagation();
    Dropdown.collapse(owner);
  }

  document.addEventListener(
    'touchstart',
    (evt) => {
      const t = evt.target;
      const hit = resolveBackTarget(t);
      if (!hit) {
        resetTouchTrack();
        return;
      }
      const p = evt.touches && evt.touches[0];
      if (!p) {
        resetTouchTrack();
        return;
      }
      // iOS/WebKit often retargets `event.target` on `touchend` (e.g. to document or a parent),
      // so re-resolving the back control there fails. Persist the open panel from `touchstart`.
      touchTrack = { active: true, x: p.clientX, y: p.clientY, owner: hit.owner };
    },
    true
  );

  document.addEventListener(
    'touchcancel',
    () => {
      resetTouchTrack();
    },
    true
  );

  document.addEventListener(
    'touchend',
    (evt) => {
      if (!touchTrack.active) return;
      const owner = touchTrack.owner;
      const startX = touchTrack.x;
      const startY = touchTrack.y;
      resetTouchTrack();
      const p = evt.changedTouches && evt.changedTouches[0];
      if (!p) return;
      if (Math.abs(p.clientX - startX) > TAP_SLOP_PX || Math.abs(p.clientY - startY) > TAP_SLOP_PX) {
        return;
      }
      if (!owner || !owner.isConnected || !owner.classList.contains('open')) {
        return;
      }
      collapseSlideout(evt, owner);
      suppressClickUntil = Date.now() + 450;
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'click',
    (evt) => {
      const t = evt.target;
      const hit = resolveBackTarget(t);
      if (!hit) return;
      // Do not block unrelated clicks: only swallow duplicate synthetic clicks on the *same* back control
      // after a touch `touchend` (global suppress was breaking the next open on any nav item for ~450ms).
      if (Date.now() < suppressClickUntil) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }
      collapseSlideout(evt, hit.owner);
    },
    true
  );
})();

// Initialise all dropdown elements on the page
const allDropdowns = Utility.DomSelector.$$('[is="drop-down"]');
allDropdowns.forEach((ele) => {
  new Dropdown(ele);
});

//===============================================================================================================
//          Global Handler for Dropdowns and Custom Selects - Close all open on outside click                  //
//===============================================================================================================

(() => {
  /**
   * Closes all open custom-select dropdowns
   */
  const closeAllOpenSelects = () => {
    document.querySelectorAll('custom-select.open').forEach(select => {
      if (select.closeDropdown && typeof select.closeDropdown === 'function') {
        select.closeDropdown();
      }
    });
  };

  /**
   * Closes all open dropdown elements
   */
  const closeAllOpenDropdowns = () => {
    document.querySelectorAll('[is="drop-down"].open').forEach(dropdown => {
      Dropdown.collapse(dropdown);
    });
  };

  /**
   * Closes all open dropdown/select components
   */
  const closeAllOpenComponents = () => {
    closeAllOpenSelects();
    closeAllOpenDropdowns();
  };

  /**
   * Handles clicks/touches outside dropdown and custom-select elements
   * @param {Event} event - Click or touch event
   */
  const handleOutsideInteraction = (event) => {
    // Don't close if interaction is inside any dropdown or custom-select element
    if (event.target.closest('custom-select') || event.target.closest('[is="drop-down"]')) return;
    closeAllOpenComponents();
  };

  // Add global listeners for both click and touchstart (mobile support)
  document.body.addEventListener('click', handleOutsideInteraction);
  document.body.addEventListener('touchstart', handleOutsideInteraction, { passive: true });
})();

//===============================================================================================================
//          End of Global Handler for Dropdowns and Custom Selects                                              //
//===============================================================================================================
